import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppLoadingState,
  AppPage,
  AppPageIntro,
  AppSectionHeader,
} from '../../ui-foundation';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <AppCard>
      <AppCardHeader>
        <AppSectionHeader title={title} />
      </AppCardHeader>
      <AppCardBody>
        <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
      </AppCardBody>
    </AppCard>
  );
}

export default function ModerationGuidelinesPage() {
  const navigate = useNavigate();
  const { isAdminOrMod, loading, isAdmin } = useUserRole();

  useEffect(() => {
    if (!loading && !isAdminOrMod) navigate('/money', { replace: true });
  }, [isAdminOrMod, loading, navigate]);

  if (loading || !isAdminOrMod) {
    return (
      <AppPage className="animate-fade-in">
        <AppLoadingState
          title="Loading..."
          body="Checking whether this account can open the internal moderation guidance."
        />
      </AppPage>
    );
  }

  return (
    <AppPage className="animate-fade-in">
      <AppPageIntro
        title="Moderation guidance"
        subtitle="Internal-only guidance for Adulting moderators and admins."
      />

      <div className="space-y-4">
        <AppCard>
          <AppCardHeader>
            <AppSectionHeader
              title="Moderation guidance"
              subtitle="Adulting LifeOS · Last updated April 2026"
            />
          </AppCardHeader>
          <AppCardBody>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Adulting is operated by Steady House Ltd. The goal is to keep Adulting community safe, calm, and useful. Quiet, proportionate action is better than public conflict.
            </p>
          </AppCardBody>
        </AppCard>

        <Section title="Roles and limits">
          <p>Members can post, reply, report, and manage their own content only.</p>
          <p>Moderators can review reports, hide or restore community content, and mark items reviewed.</p>
          <p>Administrators can do everything moderators can, plus role changes, broader account intervention, and app-level decisions.</p>
          <p>{isAdmin ? 'As an admin, use irreversible actions rarely and deliberately.' : 'As a moderator, escalate rather than improvising if the right action is unclear.'}</p>
        </Section>

        <Section title="Principles">
          <p>Least-force first: if hiding content solves the immediate problem, do that before escalating further.</p>
          <p>Moderate the content, not the person. Stay factual and neutral.</p>
          <p>Apply the same standard regardless of who posted it.</p>
        </Section>

        <Section title="Escalate immediately when">
          <p>There are threats, coercion, stalking, doxxing, scams, exploitation, or serious safeguarding concerns.</p>
          <p>A user repeatedly breaches the rules or appears to be using the space in bad faith.</p>
          <p>You suspect someone’s account, documents, or personal data may be at risk.</p>
          <p>Serious safeguarding, privacy, or legal-risk issues should be escalated through Steady House Ltd immediately rather than handled ad hoc in community threads.</p>
        </Section>

        <Section title="Privacy boundaries">
          <p>Community moderation should stay inside community tools where possible.</p>
          <p>Moderators should not browse unrelated user data out of curiosity. Access should stay limited to what is necessary to review the report or keep the service safe.</p>
          <p>We do not sell user data, and anyone with admin access should treat all user information as confidential.</p>
        </Section>

        <Section title="Good operational habits">
          <p>Leave an action trail. Serious decisions should be visible in a moderation log.</p>
          <p>Use consistent report reasons and action labels so the queue stays easy to read.</p>
          <p>If you need a break, hand the queue over. Moderator wellbeing matters too.</p>
        </Section>

        <Section title="Current tooling truth">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Adulting now has a moderation log, member-level action history, and warning / suspend / restore flows inside the privileged tools. Use the member record as the factual action trail. The remaining work is calmer operational polish and clearer history visibility, not missing fundamentals.
          </p>
          <AppButton size="sm" tone="outline" onPress={() => navigate('/admin')}>
            Back to admin
          </AppButton>
        </Section>
      </div>
    </AppPage>
  );
}
